import type { ListResponse } from './wire';

/**
 * Every page of an FS-10 list read, as one list (BUG-109, BUG-111). A picker or filter filled from a
 * paged endpoint must offer every record it can choose, however many there are, so it reads them all
 * rather than the first page (harness `.forge/lessons.md` L-151).
 */

/**
 * The most pages `readEveryPage` reads — 5,000 records at the backend's largest page of 100. A first page
 * reporting more is refused rather than read in part: a list that silently stops short is the defect
 * BUG-109 fixed.
 */
export const EVERY_PAGE_MAX = 50;

export interface EveryPageOptions<T> {
    /** What is read, opening the error a too-long list raises — `The user directory`. */
    readonly what: string;
    /** The identifier an item is kept once by. */
    readonly idOf: (item: T) => number;
    /** Checked before each page after the first; TanStack Query aborts it when the query is cancelled. */
    readonly signal?: AbortSignal | undefined;
    /**
     * The most pages read; a first page reporting more is refused. `EVERY_PAGE_MAX` when omitted, so a
     * picker never silently stops short. F5-h's "all N" read passes `Number.POSITIVE_INFINITY`: the
     * administrator has seen the count and confirmed the pass, and refusing a large hand-out would make
     * it impossible (F5-h WI-1.4 plan Decision 3). The read is still bounded by the `totalPages` its first
     * page reports.
     */
    readonly maxPages?: number | undefined;
}

/**
 * Read page 1 with `readPage(1)`; when its envelope reports `totalPages > 1`, read pages `2…totalPages`
 * one after another (the backend's `page` is 1-based) and return them as one list.
 *
 * A single page is returned as it arrived. Several are returned as one page that holds every item:
 * `number` and `totalPages` are `1`, and `size` and `totalItems` are the number of items returned, so a
 * caller comparing `totalItems` with `items.length` sees nothing left out.
 *
 * A failing page rejects the whole read, so a failure never returns a partial list. The pages are not one
 * snapshot, though: a record added or removed while they are read can shift the others between pages, so
 * one may be missed until the next read, and one listed on two pages is kept once, where first seen. A
 * `signal` aborted after a page stops the read before the next one is requested. A first page reporting
 * more than `maxPages` pages (default `EVERY_PAGE_MAX`) is refused before any other page is read.
 */
export async function readEveryPage<T>(
    readPage: (page: number) => Promise<ListResponse<T>>,
    { what, idOf, signal, maxPages = EVERY_PAGE_MAX }: EveryPageOptions<T>,
): Promise<ListResponse<T>> {
    const first = await readPage(1);
    const { totalPages } = first.page;
    if (totalPages <= 1) {
        return first;
    }
    if (totalPages > maxPages) {
        throw new Error(`${what} reported ${totalPages} pages, more than ${maxPages} pages it will read`);
    }
    const byId = new Map<number, T>(first.items.map((item) => [idOf(item), item]));
    for (let page = 2; page <= totalPages; page += 1) {
        signal?.throwIfAborted();
        for (const item of (await readPage(page)).items) {
            const id = idOf(item);
            if (!byId.has(id)) {
                byId.set(id, item);
            }
        }
    }
    const items = [...byId.values()];
    return { items, page: { number: 1, size: items.length, totalItems: items.length, totalPages: 1 } };
}
